import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  Globe,
  Plus,
  Search,
  MapPin,
  Users,
  Target,
  TrendingUp,
  Filter,
  X,
  ChevronRight,
  Award,
  Heart,
  Edit,
  Trash2,
} from "lucide-react";

// ODS Goals
const odsGoals = [
  { id: 1, name: "Fin de la pobreza", color: "#E5243B" },
  { id: 2, name: "Hambre cero", color: "#DDA63A" },
  { id: 3, name: "Salud y bienestar", color: "#4C9F38" },
  { id: 4, name: "Educación de calidad", color: "#C5192D" },
  { id: 5, name: "Igualdad de género", color: "#FF3A21" },
  { id: 6, name: "Agua limpia", color: "#26BDE2" },
  { id: 7, name: "Energía asequible", color: "#FCC30B" },
  { id: 8, name: "Trabajo decente", color: "#A21942" },
  { id: 9, name: "Industria e innovación", color: "#FD6925" },
  { id: 10, name: "Reducción de desigualdades", color: "#DD1367" },
  { id: 11, name: "Ciudades sostenibles", color: "#FD9D24" },
  { id: 12, name: "Consumo responsable", color: "#BF8B2E" },
  { id: 13, name: "Acción por el clima", color: "#3F7E44" },
  { id: 14, name: "Vida submarina", color: "#0A97D9" },
  { id: 15, name: "Vida de ecosistemas", color: "#56C02B" },
  { id: 16, name: "Paz y justicia", color: "#00689D" },
  { id: 17, name: "Alianzas", color: "#19486A" },
];


const WCAImpact = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("comunidades");
  const [projects, setProjects] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCommunityModal, setShowCommunityModal] = useState(false);
  const [editingCommunity, setEditingCommunity] = useState(null);
  const [newCommunity, setNewCommunity] = useState({
    name: "",
    location: "",
    beneficiaries: 0,
    programs: 0,
    ods_goals: [],
    status: "active",
  });

  const isSuperAdmin = user?.is_super_admin || user?.role === "admin";

  const [impactMetrics, setImpactMetrics] = useState([]);

  useEffect(() => {
    fetchProjects();
    fetchCommunities();
    fetchImpactMetrics();
  }, []);

  const fetchCommunities = async () => {
    try {
      const response = await axios.get(`${API}/communities`, { withCredentials: true });
      setCommunities(response.data || []);
    } catch (error) {
      // Silently handle 404 errors as endpoints may not be implemented yet
      if (error.response?.status !== 404) {
        console.error("Error fetching communities:", error);
      }
      setCommunities([]);
    }
  };

  const fetchImpactMetrics = async () => {
    try {
      const response = await axios.get(`${API}/impact/metrics`, { withCredentials: true });
      setImpactMetrics(response.data || []);
    } catch (error) {
      // Silently handle 404 errors as endpoints may not be implemented yet
      if (error.response?.status !== 404) {
        console.error("Error fetching impact metrics:", error);
      }
      setImpactMetrics([]);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects?division=impact`, {
        withCredentials: true,
      });
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCommunity = async () => {
    if (!newCommunity.name.trim()) return;
    const newComm = {
      id: `comm_${Date.now()}`,
      ...newCommunity,
      image: "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=400",
    };
    setCommunities([...communities, newComm]);
    setShowCommunityModal(false);
    setEditingCommunity(null);
    setNewCommunity({
      name: "",
      location: "",
      beneficiaries: 0,
      programs: 0,
      ods_goals: [],
      status: "active",
    });
  };

  const handleEditCommunity = (community) => {
    setEditingCommunity(community);
    setNewCommunity({
      name: community.name,
      location: community.location,
      beneficiaries: community.beneficiaries,
      programs: community.programs,
      ods_goals: community.ods_goals || [],
      status: community.status || "active",
    });
    setShowCommunityModal(true);
  };

  const handleUpdateCommunity = async () => {
    if (!editingCommunity) return;
    setCommunities(communities.map(c => 
      c.id === editingCommunity.id
        ? { ...c, ...newCommunity }
        : c
    ));
    setShowCommunityModal(false);
    setEditingCommunity(null);
    setNewCommunity({
      name: "",
      location: "",
      beneficiaries: 0,
      programs: 0,
      ods_goals: [],
      status: "active",
    });
  };

  const handleDeleteCommunity = async (communityId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta comunidad?")) return;
    setCommunities(communities.filter(c => c.id !== communityId));
  };

  const getODSGoal = (id) => odsGoals.find((g) => g.id === id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wca-impact">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center">
            <Globe size={28} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">WCA | Impact</h1>
            <p className="text-slate-500">Mapa de comunidades y métricas ODS</p>
          </div>
        </div>
      </div>

      {/* Impact Metrics */}
      {impactMetrics.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {impactMetrics.map((metric, index) => (
          <div key={index} className="glass-panel p-6" data-testid={`impact-metric-${index}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-rose-100 flex items-center justify-center">
                <metric.icon size={24} className="text-rose-600" />
              </div>
              <span className="flex items-center gap-1 text-emerald-600 text-sm font-medium">
                <TrendingUp size={16} />
                {metric.change}
              </span>
            </div>
            <p className="text-3xl font-bold text-slate-800">{metric.value}</p>
            <p className="text-slate-500 mt-1">{metric.label}</p>
          </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="glass-panel p-1 inline-flex rounded-full">
        {[
          { id: "comunidades", label: "Comunidades", icon: MapPin },
          { id: "ods", label: "Mapa ODS", icon: Target },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
              activeTab === tab.id
                ? "bg-primary text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Comunidades Tab */}
      {activeTab === "comunidades" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Buscar comunidades..."
                className="w-full glass-input pl-10"
                data-testid="search-communities-input"
              />
            </div>
            <button className="btn-primary flex items-center gap-2" data-testid="add-community-btn">
              <Plus size={20} />
              Nueva Comunidad
            </button>
          </div>

          {communities.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {communities.map((community) => (
              <div
                key={community.id}
                className="glass-panel overflow-hidden card-hover"
                data-testid={`community-${community.id}`}
              >
                <div className="aspect-video relative">
                  <img
                    src={community.image}
                    alt={community.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3">
                    <span className={`badge ${
                      community.status === "active" ? "badge-success" : "badge-warning"
                    }`}>
                      {community.status === "active" ? "Activo" : "Planificación"}
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="font-semibold text-slate-800 mb-1">{community.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                    <MapPin size={14} />
                    {community.location}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center p-3 rounded-xl bg-slate-50">
                      <p className="text-lg font-bold text-slate-800">{community.beneficiaries}</p>
                      <p className="text-xs text-slate-500">Beneficiarios</p>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-slate-50">
                      <p className="text-lg font-bold text-slate-800">{community.programs}</p>
                      <p className="text-xs text-slate-500">Programas</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {community.ods_goals && community.ods_goals.length > 0 ? (
                      community.ods_goals.map((odsId) => {
                        const ods = getODSGoal(odsId);
                        return ods ? (
                          <span
                            key={odsId}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-white text-xs font-medium"
                            style={{ backgroundColor: ods.color }}
                          >
                            ODS {ods.id}
                          </span>
                        ) : null;
                      })
                    ) : (
                      <span className="text-xs text-slate-400">Sin ODS asignados</span>
                    )}
                  </div>
                  {isSuperAdmin && (
                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2">
                      <button
                        onClick={() => handleEditCommunity(community)}
                        className="flex-1 btn-secondary py-2 text-sm flex items-center justify-center gap-2"
                      >
                        <Edit size={14} />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteCommunity(community.id)}
                        className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              ))}
            </div>
          ) : (
            <div className="glass-panel p-12 text-center">
              <MapPin size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay comunidades registradas</p>
            </div>
          )}
        </div>
      )}

      {/* ODS Map Tab */}
      {activeTab === "ods" && (
        <div className="space-y-6">
          <div className="glass-panel p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-6">
              Objetivos de Desarrollo Sostenible (ODS)
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {odsGoals.map((ods) => {
                const isActive = communities.some((c) => c.ods_goals && c.ods_goals.includes(ods.id));
                return (
                  <div
                    key={ods.id}
                    className={`p-4 rounded-2xl text-center transition-all cursor-pointer ${
                      isActive
                        ? "ring-2 ring-offset-2"
                        : "opacity-50 grayscale hover:opacity-100 hover:grayscale-0"
                    }`}
                    style={{
                      backgroundColor: `${ods.color}20`,
                      ringColor: isActive ? ods.color : "transparent",
                    }}
                    data-testid={`ods-${ods.id}`}
                  >
                    <div
                      className="w-12 h-12 rounded-xl mx-auto mb-2 flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: ods.color }}
                    >
                      {ods.id}
                    </div>
                    <p className="text-xs font-medium text-slate-700 line-clamp-2">
                      {ods.name}
                    </p>
                    {isActive && (
                      <div className="mt-2 flex items-center justify-center gap-1">
                        <Award size={12} style={{ color: ods.color }} />
                        <span className="text-xs" style={{ color: ods.color }}>
                          Activo
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ODS Impact Summary */}
          <div className="glass-panel p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-6">
              Resumen de Impacto por ODS
            </h2>
            <div className="space-y-4">
              {odsGoals
                .filter((ods) => communities.some((c) => c.ods_goals && c.ods_goals.includes(ods.id)))
                .map((ods) => {
                  const communitiesWithODS = communities.filter((c) =>
                    c.ods_goals && c.ods_goals.includes(ods.id)
                  );
                  const totalBeneficiaries = communitiesWithODS.reduce(
                    (sum, c) => sum + (c.beneficiaries || 0),
                    0
                  );
                  const maxBeneficiaries = Math.max(...communities.map(c => c.beneficiaries || 0), 1);

                  return (
                    <div
                      key={ods.id}
                      className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50"
                    >
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0"
                        style={{ backgroundColor: ods.color }}
                      >
                        {ods.id}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-slate-800">{ods.name}</h3>
                        <p className="text-sm text-slate-500">
                          {communitiesWithODS.length} comunidades • {totalBeneficiaries.toLocaleString()} beneficiarios
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="h-2 w-24 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.min((totalBeneficiaries / maxBeneficiaries) * 100, 100)}%`,
                              backgroundColor: ods.color,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              {odsGoals.filter((ods) => communities.some((c) => c.ods_goals && c.ods_goals.includes(ods.id))).length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <Target size={32} className="mx-auto mb-2" />
                  <p className="text-sm">No hay ODS activos. Agrega comunidades con ODS asignados para ver el resumen.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WCAImpact;
